import { nanoid } from 'nanoid'

/**
 * @typedef {'top'|'top-start'|'top-end'|'right'|'right-start'|'right-end'|'bottom'|'bottom-start'|'bottom-end'|'left'|'left-start'|'left-end'} Placement
 *
 * @typedef {object} TooltipOptions
 * @property {string|HTMLElement} [content]  Text, HTML (when allowHTML) or an element to show
 * @property {boolean} [allowHTML]           Render `content` as HTML instead of text
 * @property {boolean} [interactive]         Keep the tooltip open while the pointer is inside it
 * @property {Placement} [placement]         Preferred placement, floating-ui naming
 * @property {number} [maxWidth]             Max width in px
 * @property {number} [hideDelay]            Delay in ms before hiding after the pointer leaves
 * @property {'hover'|'manual'} [trigger]    'manual' disables all automatic show/hide listeners
 *
 * @typedef {object} TooltipInstance
 * @property {() => void} show                                        Open the tooltip (no-op when disabled or empty)
 * @property {() => void} hide                                        Close the tooltip
 * @property {() => void} enable                                      Allow the tooltip to open again
 * @property {() => void} disable                                     Close and block the tooltip from opening
 * @property {(content: string|HTMLElement) => void} setContent      Replace the content, re-rendering if open
 * @property {(props: Partial<TooltipOptions>) => void} setProps     Merge new options, rebinding triggers if needed
 * @property {() => void} destroy                                     Remove listeners, attributes and the element
 */

/** @type {Required<Omit<TooltipOptions, 'content'>>} */
const defaultOptions = {
	allowHTML: false,
	interactive: false,
	placement: 'top',
	maxWidth: 600,
	hideDelay: 100,
	trigger: 'hover',
}

// Minimum gap between the tooltip and the edge of the viewport or its scroll container, in px.
// Wide enough to keep the box-shadow (14px blur) fully visible; getBoundingClientRect() does not include it.
const BOUNDS_MARGIN = 16

export const FOCUSABLE_SELECTOR =
	'a[href], button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"])'

/**
 * Create a tooltip for a reference element, rendered as a `popover="manual"` element in the top layer
 * @param {HTMLElement} reference - The element the tooltip belongs to
 * @param {TooltipOptions} [options] - Tooltip options
 * @returns {TooltipInstance} - The tooltip instance
 */
export default function createTooltip(reference, options = {}) {
	/** @type {Required<TooltipOptions>} */
	let props = { content: '', ...defaultOptions, ...options }
	let enabled = true
	/** @type {ReturnType<typeof setTimeout> | null} */
	let hideTimer = null

	const id = `tooltip_${nanoid(8)}`

	// Created lazily on the first show(): a table can carry hundreds of v-tip cells, most of which are never hovered,
	// and each eager element would be a hidden <div> in <body>. Null until then; every use below guards for it.
	/** @type {HTMLDivElement|null} */
	let tooltipEl = null

	// Keyboard focus lives on the nearest focusable ancestor (e.g. the <a> around a TruncateText),
	// so the tooltip borrows that focus instead of adding a second tab stop. Pointer events stay on the reference.
	const focusHost = reference.closest(FOCUSABLE_SELECTOR) ?? reference

	reference.style.anchorName = `--${id}`

	/**
	 * Create and attach the tooltip element the first time it is needed.
	 * @returns {HTMLDivElement} - The tooltip element
	 */
	function ensureTooltipEl() {
		if (tooltipEl) {
			return tooltipEl
		}
		tooltipEl = document.createElement('div')
		tooltipEl.id = id
		tooltipEl.className = 'nfo-tooltip'
		tooltipEl.setAttribute('role', 'tooltip')
		tooltipEl.setAttribute('popover', 'manual')
		tooltipEl.style.positionAnchor = `--${id}`
		tooltipEl.dataset.placement = props.placement
		document.body.append(tooltipEl)

		focusHost.setAttribute('aria-describedby', id)
		bindTooltipListeners()

		return tooltipEl
	}

	// ---- rendering -------------------------------------------------------------------------------------------------

	function renderContent() {
		tooltipEl.style.setProperty('--tooltip-max-width', `${props.maxWidth}px`)

		if (props.content instanceof HTMLElement) {
			tooltipEl.replaceChildren(props.content)
			return
		}
		// show() only calls this after hasContent(), so content is a non-empty string here.
		if (props.allowHTML) {
			tooltipEl.innerHTML = String(props.content)
			return
		}
		tooltipEl.textContent = String(props.content)
	}

	function isOpen() {
		return tooltipEl?.matches(':popover-open') ?? false
	}

	// ---- show / hide -----------------------------------------------------------------------------------------------

	function clearHideTimer() {
		if (hideTimer) {
			clearTimeout(hideTimer)
			hideTimer = null
		}
	}

	function show() {
		clearHideTimer()
		if (!enabled || isOpen() || !hasContent()) {
			return
		}
		ensureTooltipEl()
		renderContent()
		tooltipEl.showPopover()
		keepInBounds()
	}

	function hide() {
		clearHideTimer()
		if (!isOpen()) {
			return
		}
		// Leave `translate` in place: the popover keeps fading out for a moment and would visibly jump back.
		// keepInBounds() resets it before measuring on the next show.
		tooltipEl.hidePopover()
	}

	/**
	 * The nearest ancestor that scrolls horizontally (e.g. `.table-responsive`), or null.
	 * @returns {HTMLElement|null} - The scroll container
	 */
	function findScrollParent() {
		for (let node = reference.parentElement; node; node = node.parentElement) {
			const { overflowX } = getComputedStyle(node)
			if (overflowX === 'auto' || overflowX === 'scroll') {
				return node
			}
		}
		return null
	}

	/**
	 * Keep the tooltip horizontally on screen. This is load-bearing, not cosmetic: in practice the browser does
	 * NOT shift an anchor-centred popover back into the viewport (a cell scrolled half out of a table anchors the
	 * tooltip over its hidden part, and narrow screens leave it hanging past the edge). Nudge it so it stays within
	 * the visible band of its scroll container, or the viewport when it does not fit that band. Runs once per show;
	 * `translate` is applied after layout, so it does not disturb the anchor position or the flip fallbacks.
	 */
	function keepInBounds() {
		tooltipEl.style.translate = ''
		const rect = tooltipEl.getBoundingClientRect()
		if (rect.width === 0) {
			return
		}

		// Start from the viewport, measured without the vertical scrollbar (`innerWidth` and `100dvw` include it).
		const viewportWidth = document.documentElement.clientWidth
		let left = BOUNDS_MARGIN
		let right = viewportWidth - BOUNDS_MARGIN
		const scrollerRect = findScrollParent()?.getBoundingClientRect()
		if (scrollerRect) {
			const bandLeft = Math.max(scrollerRect.left, 0) + BOUNDS_MARGIN
			const bandRight = Math.min(scrollerRect.right, viewportWidth) - BOUNDS_MARGIN
			if (rect.width <= bandRight - bandLeft) {
				// Fits inside the scroll container: stay within its visible band.
				left = bandLeft
				right = bandRight
			} else if (bandLeft + rect.width <= right) {
				// Wider than the container but not the viewport: keep the left edge on the container so the tooltip
				// stays visually attached to the table instead of overhanging it on both sides.
				left = bandLeft
			}
		}

		// Too wide even for the viewport: pin the left edge so at least the start of the text is readable.
		let shift = 0
		if (rect.width > right - left || rect.left < left) {
			shift = left - rect.left
		} else if (rect.right > right) {
			shift = right - rect.right
		}
		if (shift !== 0) {
			tooltipEl.style.translate = `${Math.round(shift)}px 0`
		}
	}

	function scheduleHide() {
		clearHideTimer()
		hideTimer = setTimeout(hide, props.hideDelay)
	}

	function hasContent() {
		return props.content instanceof HTMLElement || String(props.content ?? '').length > 0
	}

	// ---- triggers --------------------------------------------------------------------------------------------------

	/**
	 * @param {KeyboardEvent} event - The keydown event
	 */
	function handleKeydown(event) {
		if (event.key === 'Escape') {
			hide()
		}
	}

	/**
	 * @param {FocusEvent} event - The focusout event
	 */
	function handleFocusOut(event) {
		// Focus moving from the trigger into an interactive tooltip keeps it open.
		if (!tooltipEl?.contains(/** @type {Node|null} */ (event.relatedTarget))) {
			scheduleHide()
		}
	}

	/**
	 * Focus leaving an interactive tooltip closes it, unless it returns to the trigger.
	 * @param {FocusEvent} event - The focusout event on the tooltip element
	 */
	function handleTooltipFocusOut(event) {
		const next = /** @type {Node|null} */ (event.relatedTarget)
		if (!tooltipEl.contains(next) && !focusHost.contains(next)) {
			scheduleHide()
		}
	}

	/** @param {PointerEvent} event */
	function handlePointerToggle(event) {
		if (event.pointerType !== 'touch') {
			return
		}
		if (isOpen()) {
			hide()
		} else {
			show()
		}
	}

	/** @param {PointerEvent} event */
	function handlePointerEnter(event) {
		if (event.pointerType !== 'touch') {
			show()
		}
	}

	/** @param {PointerEvent} event */
	function handlePointerLeave(event) {
		if (event.pointerType !== 'touch') {
			scheduleHide()
		}
	}

	function bindTriggers() {
		if (props.trigger === 'manual') {
			return
		}
		focusHost.addEventListener('focusin', show)
		focusHost.addEventListener('focusout', handleFocusOut)
		focusHost.addEventListener('keydown', handleKeydown)
		reference.addEventListener('pointerenter', handlePointerEnter)
		reference.addEventListener('pointerleave', handlePointerLeave)
		reference.addEventListener('pointerdown', handlePointerToggle)
	}

	function unbindTriggers() {
		focusHost.removeEventListener('focusin', show)
		focusHost.removeEventListener('focusout', handleFocusOut)
		focusHost.removeEventListener('keydown', handleKeydown)
		reference.removeEventListener('pointerenter', handlePointerEnter)
		reference.removeEventListener('pointerleave', handlePointerLeave)
		reference.removeEventListener('pointerdown', handlePointerToggle)
	}

	/**
	 * Listeners on the tooltip element itself. Bound when the element is created and re-bound when `interactive`
	 * changes. Interactive only: keep the tooltip open while the pointer or focus is inside it.
	 * Non-interactive tooltips also get `pointer-events: none` via the class, so they never catch the pointer.
	 */
	function bindTooltipListeners() {
		if (!tooltipEl) {
			return
		}
		tooltipEl.classList.toggle('is-interactive', props.interactive)
		if (props.interactive && props.trigger !== 'manual') {
			tooltipEl.addEventListener('pointerenter', clearHideTimer)
			tooltipEl.addEventListener('pointerleave', scheduleHide)
			tooltipEl.addEventListener('focusout', handleTooltipFocusOut)
		}
	}

	function unbindTooltipListeners() {
		if (!tooltipEl) {
			return
		}
		tooltipEl.removeEventListener('pointerenter', clearHideTimer)
		tooltipEl.removeEventListener('pointerleave', scheduleHide)
		tooltipEl.removeEventListener('focusout', handleTooltipFocusOut)
	}

	bindTriggers()

	// ---- instance API ----------------------------------------------------------------------------------------------

	return {
		show,
		hide,
		enable() {
			enabled = true
		},
		disable() {
			enabled = false
			hide()
		},
		setContent(content) {
			props.content = content
			if (isOpen()) {
				renderContent()
			}
		},
		setProps(newProps) {
			const triggerChanged =
				(newProps.trigger !== undefined && newProps.trigger !== props.trigger) ||
				(newProps.interactive !== undefined && newProps.interactive !== props.interactive)
			props = { ...props, ...newProps }
			if (newProps.placement && tooltipEl) {
				tooltipEl.dataset.placement = props.placement
			}
			if (triggerChanged) {
				unbindTriggers()
				unbindTooltipListeners()
				bindTriggers()
				bindTooltipListeners()
			}
			if (isOpen()) {
				renderContent()
			}
		},
		destroy() {
			hide()
			unbindTriggers()
			unbindTooltipListeners()
			focusHost.removeAttribute('aria-describedby')
			tooltipEl?.remove()
			tooltipEl = null
		},
	}
}
